import { emptyArray, listMemberForId, setNavAdvisoryLockout, useForceRender } from "../util/utils";
import { useEffect, useRef, useState } from "react";
import { getDB } from "../util/db";
import { Button, FormSelect } from "react-bootstrap";
import { LoadingComponent } from "../components/LoadingComponent";
import { Timestamp } from "../components/Timestamp";
import { CloudCheck, Eye, PencilSquare, Search, PlusLg } from "react-bootstrap-icons";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Inspection } from "../models/Inspection";
import { RentalStatusDisplay } from "../components/RentalStatusDisplay";
import { useProfile } from "../components/ProfileContext";
import { SpinnerButton } from "../components/SpinnerButton";
import { ButtonValueSelect } from "../components/ButtonValueSelect";
import { ObjectSelector } from "../components/ObjectSelector";
import { SharingStatus } from "../components/SharingStatus";

export function InspectionListingPage(props) {
	setNavAdvisoryLockout(false);

	let [search] = useSearchParams();
	let navigate = useNavigate();
	let selectRef = useRef();
    let [inspectionsLoaded, setInspectionsLoaded] = useState(false);
    let [inspectionList, setInspectionList] = useState(emptyArray);
	let [surveys, setSurveys] = useState(emptyArray);
	let [activeJobs, setActiveJobs] = useState(emptyArray);
	let [isSyncing, setIsSyncing] = useState(false);
	let [profile] = useProfile();
	let viewModeOptions = [["All", "all"], ["In Progress", "in_progress"], ["Completed", "completed"], ["Pending Upload", "unsynchronized"]];
	let viewMode = search.get("mode");

	let unsynchronizedInspections = inspectionList.filter(x => Inspection.syncNeededFor(x));
	let filteredInspections = inspectionList.filter(inspData => {
		if(viewMode == "in_progress") {
			return (inspData.syncStatus == "EDIT" || inspData.syncStatus == "NEW") && (!Inspection.syncNeededFor(inspData));
		}
		if(viewMode == "completed") {
			return (inspData.syncStatus == "SAVED")
		}
		if(viewMode == "unsynchronized") {
			return Inspection.syncNeededFor(inspData);
		}
		
		return inspData.syncStatus != "READONLY";
	});

	useEffect(async () => {
		if(inspectionsLoaded == false || inspectionList == emptyArray) {
			let db = await getDB();
			let jobList = await db.getAll("Jobs");
			setActiveJobs(jobList);
			let inspList = await db.getAll("Inspections");
			setInspectionList(inspList.filter(i => i.syncStatus != "READONLY"));
			let surveyList = await db.getAll("Surveys");
			setSurveys(surveyList);
			setInspectionsLoaded(true);
		}
	});

    let unsubmittedInspections = [];
    let submittedInspections = [];
    inspectionList.forEach(insp => {
        if(insp.completedAt == null) {
            unsubmittedInspections.push(insp);
        } else {
            submittedInspections.push(insp);
        }
    });

    let deleteAllAction = async () => {
        if(confirm("Are you sure you want to delete all inspections from this device?  Submitted inspections will still be saved in the Vault™, but will only be available in read-only mode.")) {
            let db = await getDB();
            db.clear("Inspections");
            setInspectionsLoaded(false);    
        }
    };

	let syncPendingAction = async () => {
		setIsSyncing(true);
		setInspectionsLoaded(false);
		let hasErrors = false;
		let newInspectionList = inspectionList.concat([]);
		for(const idx in unsynchronizedInspections) {
			let inspData = unsynchronizedInspections[idx];
			let origIdx = newInspectionList.indexOf(inspData);

			let inspection = Inspection.hydrate(inspData)
			try {
				let newInspection = await inspection.sendToServer(profile);
				newInspectionList[origIdx] = newInspection;
			} catch(err) {
				console.log(err);
				hasErrors = true;
			}
		}

		if(hasErrors) {
			alert("One or more inspections failed to sync");
		}

		setInspectionList(newInspectionList);
		setIsSyncing(false);
		setInspectionsLoaded(true);
	};

	let navigateToOption = () => {
		let newViewMode = viewModeOptions[selectRef.current.selectedIndex][1];
		navigate("/inspections?mode=" + newViewMode);
	};

	return (
        <div className="inspectionListContainer">
			
			{/* <p>Below are the inspections currently saved or in-progress on your present device.</p> */}
			<h1>Latest Inspections</h1>

			<FormSelect className="my-4" ref={selectRef} onChange={navigateToOption} defaultValue={viewMode}>
				{viewModeOptions.map(vmo => <option value={vmo[1]} key={vmo[1]}>{vmo[0]}</option>)}
			</FormSelect>

			{ !inspectionsLoaded && <LoadingComponent />}  
            { inspectionsLoaded && <>
				<InspectionListingPageTable inspections={filteredInspections} surveys={surveys} jobs={activeJobs} profile={profile} />

				{unsynchronizedInspections.length > 0 && (viewMode == "all" || viewMode == "" || viewMode == "unsynchronized") && <>
					<SpinnerButton variant="success" onClick={syncPendingAction} className="me-4" spinning={isSyncing}>Sync Pending Inspections ({unsynchronizedInspections.length})</SpinnerButton>
				</>}
				<div className="clButtonContainer">
					<Button className="clButton" onClick={deleteAllAction}>Clear All</Button>
				</div>
				
            </>}

			<div className="mb-4"></div>
        </div>
    );
}

function InspectionListingPageTable(props) {
	let {inspections, surveys, jobs, profile} = props;
	return <>
		<div className="clearfix"></div>
		<div className="table-responsive">
			<table className="table latestInspectionTable">
				<thead>
					<tr>
						<th></th>
						<th>Asset #</th>
						<th>Job</th>
						<th>Inspection</th>
						<th>Completion</th>
						<th>Result</th>
						<th>Sticker</th>
					</tr>
				</thead>
				<tbody>
					{inspections.map(insp => <InspectionListingPageTableRow inspection={insp} surveys={surveys} jobs={jobs} key={insp.id} profile={profile} />)}
					{inspections.length == 0 && <tr><td colSpan="7">No matching inspections found.</td></tr>}
				</tbody>
			</table>
		</div>
	</>;
}

function InspectionListingPageTableRow(props) {
	let inspData = props.inspection;
	let profile = props.profile;
	let job = listMemberForId(props.jobs, inspData.jobId);
	let survey = listMemberForId(props.surveys, inspData.surveyId);
	let surveyName = survey?.name;
	return <tr className={`${Inspection.syncNeededFor(inspData) && "table-warning"} ${(!inspData.result) && "table-secondary"}`}>
		<td>
			<Link to={`/inspections/${inspData.id}`}>
				{ (inspData.syncStatus == "EDIT" || inspData.syncStatus == "NEW") && <PencilSquare />}
				{ inspData.syncStatus == "READONLY" && <Eye />}
				{ inspData.syncStatus == "SAVED" && <CloudCheck />}
			</Link>
		</td>
		<td><Link to={`/inspections/${inspData.id}`}>{Inspection.displayNameFor(inspData) || "Asset Not Set"}</Link> <SharingStatus ownerUid={inspData.userIdentifier} profile={profile} childAutoAccess={false} />
</td>
		<td>{job?.name}</td>
		<td>{surveyName} <RentalStatusDisplay inspection={inspData} /></td>
		<td>{inspData.completedAt == null ? "Pending" : <Timestamp dateString={inspData.completedAt} formatter={profile.dateFormatter} />}</td>
		<td>{inspData.result}</td>
		<td>{inspData.externalIdentifier}</td>
	</tr>;
}